import {
  isRecurringLike,
  selectPlatformMethodsAvailable,
  useDonationFormStore,
} from "@/stores";
import { Screen, DetailsStep } from "@/types/Navigation";
import { useNavigationStore, type NavigationState } from "@/stores";

interface UseNavigate {
  screen: NavigationState["screen"];
  visibleDetailsSteps: NavigationState["visibleDetailsSteps"];
  currentDetailsStep: NavigationState["currentDetailsStep"];
  submissionResponse: NavigationState["submissionResponse"];
  navigateTo: NavigationState["setCurrentDetailsStep"];
  completeSubmission: NavigationState["completeSubmission"];
  next: () => void;
  removeCurrentDetailsStep: () => void;
  isFinalStep: boolean;
}

const finalSteps = [
  DetailsStep.CardDetails,
  DetailsStep.BankDetails,
  DetailsStep.ConfirmDetails,
];

export function useNavigate(): UseNavigate {
  const {
    screen,
    setScreen,
    visibleDetailsSteps,
    currentDetailsStep,
    submissionResponse,
    removeVisibleDetailsSteps,
    setCurrentDetailsStep,
    completeSubmission,
  } = useNavigationStore();
  const {
    cardEnabled,
    donationType,
    paymentMethod,
    recurringDonationEnabled,
    recurringPaymentMethodsEnabled,
  } = useDonationFormStore();

  // #984/#985: a recurring donation is forced down the Direct Debit / bank-details
  // path whenever platform (card/wallet) methods aren't offered for it.
  const platformMethodsAvailable = selectPlatformMethodsAvailable({
    donationType,
    recurringDonationEnabled,
    recurringPaymentMethodsEnabled,
  });
  const forcedBankDetails =
    isRecurringLike(donationType) && !platformMethodsAvailable;

  return {
    screen,
    visibleDetailsSteps,
    currentDetailsStep,
    submissionResponse,
    completeSubmission,
    next: () => {
      if (screen === Screen.Amount) {
        const destinedStep = cardEnabled
          ? DetailsStep.SelectCard
          : DetailsStep.DonationInformation;

        setScreen(Screen.Details);
        setCurrentDetailsStep(destinedStep);
        return;
      }

      switch (currentDetailsStep) {
        case DetailsStep.SelectCard:
          setCurrentDetailsStep(DetailsStep.DonationInformation);
          break;
        case DetailsStep.DonationInformation:
          if (forcedBankDetails) {
            setCurrentDetailsStep(DetailsStep.YourDetails);
          } else {
            setCurrentDetailsStep(DetailsStep.PaymentType);
          }
          break;
        case DetailsStep.PaymentType: {
          removeVisibleDetailsSteps([
            DetailsStep.CardDetails,
            DetailsStep.BankDetails,
            DetailsStep.ConfirmDetails,
          ]);
          setCurrentDetailsStep(DetailsStep.YourDetails);
          break;
        }
        case DetailsStep.YourDetails:
          setCurrentDetailsStep(DetailsStep.YourAddress);
          break;
        case DetailsStep.YourAddress:
          if (paymentMethod === "DD" || forcedBankDetails) {
            setCurrentDetailsStep(DetailsStep.BankDetails);
          } else if (paymentMethod === "CC") {
            setCurrentDetailsStep(DetailsStep.CardDetails);
          } else {
            setCurrentDetailsStep(DetailsStep.ConfirmDetails);
          }
          break;
        case DetailsStep.BankDetails:
        case DetailsStep.CardDetails:
        case DetailsStep.ConfirmDetails:
          setScreen(Screen.Success);
          return;
        default:
          throw new Error(
            `Unexpected currentDetailsStep: ${currentDetailsStep}`,
          );
      }
    },
    navigateTo: setCurrentDetailsStep,
    removeCurrentDetailsStep: () => setCurrentDetailsStep(null),
    isFinalStep: currentDetailsStep
      ? finalSteps.includes(currentDetailsStep)
      : false,
  };
}
